import { MAINTENANCE_MESSAGE } from "../lib/config";

// Hand-tuned star field, computed once at module load so it never re-shuffles
// on render. Deterministic on purpose (a sine hash, not Math.random) so the
// sky looks identical every visit instead of jittering between paints.
const STARS = Array.from({ length: 110 }, (_, i) => {
  const hash = (seed: number) => {
    const x = Math.sin((i + 1) * seed) * 10_000;
    return x - Math.floor(x); // 0..1
  };
  return {
    top: hash(12.9898) * 100,
    left: hash(78.233) * 100,
    size: 0.8 + hash(43.123) * 2.2, // a few big bright ones among the dust
    rest: 0.08 + hash(7.11) * 0.42, // visible-but-dim resting opacity
    dur: 3.2 + hash(19.7) * 4.5,
    delay: hash(3.31) * 6,
  };
});

/**
 * Full-screen "the site has gone to sleep" takeover, rendered in place of the
 * whole app while `MAINTENANCE` is on (see lib/config). The backend is resting,
 * not broken — so this reads calm and nocturnal rather than alarming: the
 * brand's little accent dot has grown into one softly breathing orb, dozing in
 * a star field. A rarely-seen screen, so it gets the splurge.
 */
export function MaintenanceScreen() {
  return (
    <main className="relative grid min-h-screen place-items-center overflow-hidden px-6 text-center">
      <Starfield />

      {/* aurora pooled behind the sleeping orb — the only warmth in the room */}
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-1/2 h-[640px] w-[640px] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-70 blur-[90px]"
        style={{
          background:
            "radial-gradient(circle, rgba(157,123,255,0.20), rgba(91,159,214,0.05) 55%, transparent 70%)",
        }}
      />

      {/* a real block container with a width — text wraps against this, instead
          of a flex column whose items size to max-content and overflow narrow
          screens. max-w-xl keeps the headline one line on desktop; on mobile
          the column is just the viewport, so everything wraps cleanly. */}
      <div className="relative z-10 mx-auto w-full max-w-xl">
        {/* identity */}
        <p
          className="fade-up font-mono text-[13px] tracking-tight text-dim"
          style={{ animationDelay: "60ms" }}
        >
          watch<span className="text-accent">.</span>party
        </p>

        {/* the sleeping signature — the brand's accent dot grown into a
            crescent moon, breathing in the dark */}
        <div
          className="fade-up relative my-12 grid place-items-center"
          style={{ animationDelay: "160ms" }}
        >
          {/* crescent moon as vector SVG — stays crisp at any DPI (a CSS mask
              rasterizes and looks soft). The cut-out side is fully transparent
              so the night shows through, and the breathing glow (drop-shadow on
              the svg) follows the crescent's alpha, so only the moon glows. */}
          <svg
            aria-hidden
            width="76"
            height="76"
            viewBox="0 0 76 76"
            className="moon-breathe"
            style={{
              filter:
                "drop-shadow(0 0 10px rgba(157,123,255,0.5)) drop-shadow(0 0 26px rgba(157,123,255,0.28))",
            }}
          >
            <defs>
              <mask id="moon-cut">
                <rect width="76" height="76" fill="black" />
                <circle cx="38" cy="38" r="36" fill="white" />
                <circle cx="25" cy="29" r="33" fill="black" />
              </mask>
              <radialGradient id="moon-fill" cx="0.66" cy="0.46" r="0.7">
                <stop offset="0" stopColor="#b9a3ff" />
                <stop offset="1" stopColor="#9d7bff" />
              </radialGradient>
            </defs>
            <circle
              cx="38"
              cy="38"
              r="36"
              fill="url(#moon-fill)"
              mask="url(#moon-cut)"
            />
          </svg>
        </div>

        <h1
          className="fade-up display text-[28px] text-fg sm:text-[40px]"
          style={{ animationDelay: "300ms" }}
        >
          We've gone to sleep.
        </h1>

        {/* the one editable knob (lib/config) */}
        <p
          className="fade-up mx-auto mt-5 max-w-md text-[15px] leading-relaxed text-dim"
          style={{ animationDelay: "400ms" }}
        >
          {MAINTENANCE_MESSAGE}
        </p>

        {/* calm status — this dot breathes in lockstep with the orb above it */}
        <div
          className="fade-up mt-10 flex items-center justify-center gap-2.5"
          style={{ animationDelay: "520ms" }}
        >
          <span className="breathe h-1.5 w-1.5 rounded-full bg-accent" />
        </div>
      </div>
    </main>
  );
}

function Starfield() {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 overflow-hidden"
    >
      {STARS.map((s, i) => (
        <span
          key={i}
          className="twinkle absolute rounded-full bg-fg"
          style={{
            top: `${s.top}%`,
            left: `${s.left}%`,
            height: `${s.size}px`,
            width: `${s.size}px`,
            opacity: s.rest,
            animationDuration: `${s.dur}s`,
            animationDelay: `${s.delay}s`,
          }}
        />
      ))}
    </div>
  );
}
